/**
 * FreshCards Theme Context
 * Provides theme management and switching functionality.
 *
 * Two orthogonal dimensions are managed here:
 *  1. *Mode*  — light / dark (system-level contrast)
 *  2. *Skin*  — product-level palette (FreshCards Slate / FreshVibe Studio)
 *
 * The skin is applied on top of the mode theme by overriding the brand-aware
 * tokens (accent, border.focused). All other tokens (spacing, radii, etc.)
 * come from the base mode theme.
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { Theme, ThemeName, themes, Skin, SkinId, skins, DEFAULT_SKIN } from './theme-config';

interface ThemeContextType {
  theme: Theme;
  themeName: ThemeName;
  skin: Skin;
  skinId: SkinId;
  setTheme: (themeName: ThemeName) => void;
  setSkin: (skinId: SkinId) => void;
  toggleTheme: () => void;
  isDark: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: ThemeName;
  defaultSkin?: SkinId;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultTheme = 'light',
  defaultSkin = DEFAULT_SKIN,
}) => {
  const [themeName, setThemeName] = useState<ThemeName>(defaultTheme);
  const [skinId, setSkinId] = useState<SkinId>(defaultSkin);
  const [isMounted, setIsMounted] = useState(false);

  // Initialize from localStorage on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('freshcards-theme') as ThemeName | null;
    const savedSkin = localStorage.getItem('freshcards-skin') as SkinId | null;

    if (savedTheme && themes[savedTheme]) {
      setThemeName(savedTheme);
    }
    if (savedSkin && skins[savedSkin]) {
      setSkinId(savedSkin);
    }
    setIsMounted(true);
  }, []);

  // Apply mode theme + skin to CSS custom properties
  useEffect(() => {
    if (!isMounted) return;

    const baseTheme = themes[themeName];
    const skin = skins[skinId];
    const root = document.documentElement;

    // 1. Apply base mode theme — sets every category at once
    Object.entries(baseTheme.colors).forEach(([category, colors]) => {
      Object.entries(colors).forEach(([name, value]) => {
        const cssVar = `--fc-${category}-${name.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
        root.style.setProperty(cssVar, value as string);
      });
    });

    // 2. Overlay the skin on top — only brand-aware tokens
    Object.entries(skin.accent).forEach(([name, value]) => {
      const cssVar = `--fc-accent-${name}`;
      root.style.setProperty(cssVar, value);
    });
    root.style.setProperty('--fc-border-focused', skin.borderFocused);
    root.style.setProperty('--fc-skin-id', skin.id);
    root.style.setProperty(
      '--fc-primary-gradient',
      `linear-gradient(135deg, ${skin.primaryGradient[0]} 0%, ${skin.primaryGradient[1]} 100%)`
    );

    // 3. Structural tokens (typography, spacing, radii, shadows, z, motion)
    Object.entries(baseTheme.typography.fontFamily).forEach(([name, value]) => {
      root.style.setProperty(`--fc-font-family-${name}`, value);
    });
    Object.entries(baseTheme.spacing).forEach(([name, value]) => {
      root.style.setProperty(`--fc-space-${name}`, value);
    });
    Object.entries(baseTheme.borderRadius).forEach(([name, value]) => {
      root.style.setProperty(`--fc-radius-${name}`, value);
    });
    Object.entries(baseTheme.shadows).forEach(([name, value]) => {
      root.style.setProperty(`--fc-shadow-${name}`, value);
    });
    Object.entries(baseTheme.zIndex).forEach(([name, value]) => {
      root.style.setProperty(`--fc-z-${name}`, value.toString());
    });
    Object.entries(baseTheme.animations.duration).forEach(([name, value]) => {
      root.style.setProperty(`--fc-animation-duration-${name}`, value);
    });
    Object.entries(baseTheme.animations.easing).forEach(([name, value]) => {
      root.style.setProperty(`--fc-animation-easing-${name}`, value);
    });

    // 4. Data attributes for any CSS rules that need to switch on theme/skin
    root.setAttribute('data-theme', themeName);
    root.setAttribute('data-skin', skinId);
    root.setAttribute('data-product', skin.product);

    // 5. Persist
    localStorage.setItem('freshcards-theme', themeName);
    localStorage.setItem('freshcards-skin', skinId);
  }, [themeName, skinId, isMounted]);

  const toggleTheme = () => {
    setThemeName(prev => (prev === 'light' ? 'dark' : 'light'));
  };

  const value: ThemeContextType = {
    theme: themes[themeName],
    themeName,
    skin: skins[skinId],
    skinId,
    setTheme: setThemeName,
    setSkin: setSkinId,
    toggleTheme,
    isDark: themeName === 'dark',
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const useThemeColors = () => {
  const { theme } = useTheme();
  return theme.colors;
};

export const useThemeTypography = () => {
  const { theme } = useTheme();
  return theme.typography;
};

export const useThemeSpacing = () => {
  const { theme } = useTheme();
  return theme.spacing;
};

export const useThemeBorderRadius = () => {
  const { theme } = useTheme();
  return theme.borderRadius;
};

export const useThemeShadows = () => {
  const { theme } = useTheme();
  return theme.shadows;
};

export const useThemeBreakpoints = () => {
  const { theme } = useTheme();
  return theme.breakpoints;
};